// services/frontend/src/hooks/useAnalysisStream.js

import { useCallback, useRef, useState } from 'react';

const MAX_SIZE_MB = 50;
const TIMEOUT_MS = 60000; // 60s

// ✨ [개선 제안 1] 이벤트 이름을 상수로 관리하여 오타 방지 및 가독성 향상
const SSE_EVENTS = {
    DUPLICATE_FOUND: 'duplicate_found',
    DUPLICATE_NOT_LINKED: 'duplicate_not_linked',
    ERROR: 'error',
    HEARTBEAT: 'heartbeat',
    COMPLETE: 'aidd-complete',
};

export const useAnalysisStream = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [duplicateReportId, setDuplicateReportId] = useState(null);
    const [summaryText, setSummaryText] = useState("");
    const [notLinkedMsg, setNotLinkedMsg] = useState("");
    
    // AbortController는 훅 내에서만 관리
    const controllerRef = useRef(null);

    // 로그 추가 함수
    const appendLog = useCallback((msg) => setLogs((prev) => [...prev, msg]), []);

    // SSE 스트림을 파싱하고 상태를 업데이트하는 핵심 로직
    const parseAndHandleSSE = useCallback(async (body) => {
        const reader = body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();
            
            let eventName = 'message'; // 기본 이벤트 타입

            for (const line of lines) {
                if (line.startsWith('event:')) {
                    eventName = line.replace('event:', '').trim();
                } else if (line.startsWith('data:')) {
                    try {
                        const dataStr = line.replace('data:', '').trim();
                        const payload = JSON.parse(dataStr);
                        
                        // [개선 제안 2] 상수를 사용하여 이벤트 처리
                        switch (eventName) {
                            case SSE_EVENTS.DUPLICATE_FOUND:
                                setDuplicateReportId(payload.report_id);
                                setIsDuplicate(true);
                                appendLog('중복 데이터가 확인되었습니다. 기존 결과로 이동할 수 있습니다.');
                                setIsLoading(false);
                                break;
                            case SSE_EVENTS.DUPLICATE_NOT_LINKED:
                                const notLinkedMessage = payload.message || '중복 파일이지만 기존 리포트를 찾지 못했습니다.';
                                setNotLinkedMsg(notLinkedMessage);
                                appendLog(notLinkedMessage);
                                setIsLoading(false);
                                break;
                            case SSE_EVENTS.ERROR:
                                appendLog(`오류: ${payload.message || '알 수 없는 오류'}`);
                                setIsLoading(false);
                                break;
                            case SSE_EVENTS.COMPLETE:
                                if (typeof payload.recommendation_overview === 'string') {
                                    setSummaryText(payload.recommendation_overview);
                                }
                                appendLog('분석이 완료되었습니다.');
                                setIsLoading(false);
                                break;
                            case SSE_EVENTS.HEARTBEAT:
                                // 하트비트는 로그에 남기지 않음
                                break;
                            default:
                                const logMessage = payload.message || JSON.stringify(payload);
                                appendLog(logMessage);
                                break;
                        }
                    } catch (e) {
                        // JSON 파싱 실패 시 무시
                    }
                    eventName = 'message'; // 다음 메시지를 위해 이벤트 이름 초기화
                }
            }
        }
    }, [appendLog]);

    // 분석을 시작하는 함수 (파일을 인자로 받음)
    const startAnalysis = useCallback(async (file) => {
        // ... (기존 onFileChange에 있던 파일 유효성 검사 로직)
        if (!file) return;
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_SIZE_MB) {
            alert(`파일이 ${MAX_SIZE_MB}MB를 초과합니다.`);
            return;
        }
        if (!file.type || !file.type.includes('pdf')) {
            alert('PDF 파일만 업로드할 수 있습니다.');
            return;
        }

        // 상태 초기화
        setIsLoading(true);
        setLogs([]);
        setIsDuplicate(false);
        setDuplicateReportId(null);
        setSummaryText("");
        setNotLinkedMsg("");

        const formData = new FormData();
        formData.append('file', file);

        // ── WebView(앱) 환경 감지 ──────────────────────────────────────────
        // window.ReactNativeWebView가 있으면 앱 내 WebView에서 실행 중이므로
        // SSE 스트리밍 대신 백그라운드 submit 방식을 사용합니다.
        const isInAppWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;

        if (isInAppWebView) {
            // 앱 환경: POST /api/v1/analysis/submit → job_id 즉시 반환
            try {
                const response = await fetch('/api/v1/analysis/submit', {
                    method: 'POST',
                    body: formData,
                });
                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.detail || '분석 요청에 실패했습니다.');
                }

                // 앱에 분석 시작 알림 → WebView 닫기
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'ANALYSIS_SUBMITTED',
                    jobId: result.job_id,
                    status: result.status,
                }));
            } catch (err) {
                appendLog(`분석 요청 오류: ${err.message || String(err)}`);
                setIsLoading(false);
            }
            return; // 앱 환경에서는 여기서 종료 (SSE 흐름 진입 안 함)
        }

        // ── 일반 브라우저 환경: 기존 SSE 스트리밍 방식 ───────────────────
        controllerRef.current = new AbortController();
        const timeout = setTimeout(() => controllerRef.current?.abort(), TIMEOUT_MS);

        try {
            const response = await fetch('/api/v1/analysis/stream', {
                method: 'POST',
                body: formData,
                signal: controllerRef.current.signal,
            });

            if (!response.ok || !response.body) {
                throw new Error('서버로부터 올바른 스트리밍 응답을 받지 못했습니다.');
            }
            await parseAndHandleSSE(response.body);
        } catch (err) {
            if (err.name === 'AbortError') {
                appendLog('네트워크 지연으로 연결이 중단되었습니다. Wi-Fi 이용 및 재시도를 권장합니다.');
            } else {
                appendLog(`업로드/분석 중 오류: ${err.message || String(err)}`);
            }
            setIsLoading(false);
        } finally {
            clearTimeout(timeout);
        }
    }, [appendLog, parseAndHandleSSE]);

    // 분석을 중단하는 함수
    const cancelAnalysis = useCallback(() => {
        controllerRef.current?.abort();
        setIsLoading(false);
        appendLog("분석이 사용자에 의해 중단되었습니다.");
    }, [appendLog]);

    // [핵심 추가] 모달을 닫기 위한 함수를 훅 내부에 정의합니다. 
    const closeDuplicateModal = useCallback(() => {
        setIsDuplicate(false);
    }, []);
    
    // 훅이 반환하는 값들
    return {
        isLoading,
        logs,
        isDuplicate,
        duplicateReportId,
        notLinkedMsg,
        summaryText,
        startAnalysis,
        cancelAnalysis,
        closeDuplicateModal, // 반환 객체에 새로 만든 함수를 추가
    };
};